import { Controller } from "@hotwired/stimulus"

// A save can queue a background auto-translation (PT-BR → ES). The redirect
// renders before Sidekiq finishes, so the target locale's tab would sit empty
// until the editor reloaded the page by hand. This polls for the generated text
// and drops it straight into the form.
//
// Writes the same three places the "Traduzir" button does: the name input, the
// hidden description input, and the Lexical editor bound to it. Never clobbers
// text the editor typed while waiting — a field that already has content is
// left alone.
export default class extends Controller {
  static values = {
    url:        String,
    locale:     String,
    intervalMs: { type: Number, default: 3000 },
    timeoutMs:  { type: Number, default: 120000 }
  }

  connect() {
    if (!this.localeValue || !this.urlValue) return
    this._stopAt = Date.now() + this.timeoutMsValue
    this._timer = null
    this._tick()
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer)
    this._timer = null
  }

  async _tick() {
    if (Date.now() > this._stopAt) return

    try {
      const response = await fetch(
        `${this.urlValue}?locale=${encodeURIComponent(this.localeValue)}`,
        {
          headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" },
          credentials: "same-origin"
        }
      )
      if (response.ok) {
        const data = await response.json()
        if (data.name && data.name.trim()) {
          this._apply(data)
          return
        }
      }
    } catch (_err) {
      // Transient failure — keep polling until the deadline.
    }

    this._timer = setTimeout(() => this._tick(), this.intervalMsValue)
  }

  _apply(data) {
    const locale = this.localeValue

    const nameInput = document.querySelector(`input[name="translations[${locale}][name]"]`)
    if (nameInput && !nameInput.value.trim()) {
      nameInput.value = data.name
      nameInput.dispatchEvent(new Event("input", { bubbles: true }))
    }

    const descHidden = document.getElementById(`feat_desc_${locale}`)
    const descBlank = !descHidden || !this._stripHtml(descHidden.value).trim()
    if (descHidden && descBlank) descHidden.value = data.description || ""

    if (descBlank) {
      const editorEl = document.querySelector(`[data-rich-editor-for="feat_desc_${locale}"]`)
      if (editorEl) {
        const ctrl = this.application.getControllerForElementAndIdentifier(editorEl, "lexical-editor")
        if (ctrl && typeof ctrl.load === "function") ctrl.load(data.description || "")
      }
    }

    if (data.auto_translated) {
      const badge = document.querySelector(`[data-auto-translated-badge-for="${locale}"]`)
      if (badge) badge.hidden = false
    }
  }

  _stripHtml(html) {
    if (!html) return ""
    const tmp = document.createElement("div")
    tmp.innerHTML = html
    return tmp.textContent || ""
  }
}
